import {ServiceProviderModel} from "../../models/service_provider/service-provider.model";
import {Action, Selector, State, StateContext, Store} from "@ngxs/store";
import {Injectable} from "@angular/core";
import {ResetStateDefaults} from "../base-action-class.model";
import {Observable} from "rxjs/internal/Observable";
import {finalize, from, throwError} from "rxjs";
import {map} from "rxjs/operators";
import {ServiceProviderDetailAction} from "./service-provider-detail.action";
import {ServiceProviderRequestService} from "../../services/request/service-provider-request.service";

interface ServiceProviderDetailStateModel {
  id: string;
  serviceProvider: ServiceProviderModel;
  updatedServiceProvider: ServiceProviderModel;
  readonly: boolean;
  inProgress: boolean;
}

const getDefaults = () : ServiceProviderDetailStateModel => ({
  id: null,
  serviceProvider: null,
  updatedServiceProvider: null,
  readonly: false,
  inProgress: false,
});

@State<ServiceProviderDetailStateModel>({
  name: 'serviceProviderStateModel',
  defaults: getDefaults()
})
@Injectable()
export class ServiceProviderDetailXsState {

  //region CONSTRUCTOR
  constructor(
    private serviceProviderRequestService: ServiceProviderRequestService,
    private store: Store
  ) {
  }
  //endregion

  //region SELECTOR
  @Selector()
  static id(state: ServiceProviderDetailStateModel): string | null {
    return state.serviceProvider?.id;
  }

  @Selector()
  static inProgress(state: ServiceProviderDetailStateModel) : boolean | null {
    return state.inProgress;
  }

  @Selector()
  static serviceProviderId(state: ServiceProviderDetailStateModel) : string | null {
    return state.id;
  }

  @Selector()
  static serviceProvider(state: ServiceProviderDetailStateModel) : ServiceProviderModel | null {
    return state.serviceProvider;
  }

  @Selector()
  static updatedServiceProvider(state: ServiceProviderDetailStateModel) : ServiceProviderModel | null {
    return state.updatedServiceProvider;
  }
  //endregion

  //region ACTION
  @Action(ServiceProviderDetailAction.Fetch)
  public fetchServiceProvider(ctx: StateContext<ServiceProviderDetailStateModel>,
                            action: ServiceProviderDetailAction.Fetch): Observable<void> {

    const state: ServiceProviderDetailStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Service provider detail request is in progress!'));
    }
    if (!action.id) {
      return throwError(() => new Error('Service provider id cannot be null!'));
    }

    ctx.patchState({
      ...getDefaults(),
      id: action.id,
      inProgress: true
    });

    return from(this.serviceProviderRequestService.getServiceProviderById(action.id))
      .pipe(
        map((serviceProviderModel: ServiceProviderModel): void => {
          ctx.patchState({
            serviceProvider: serviceProviderModel,
            updatedServiceProvider: serviceProviderModel
          });
        }),
        finalize(() => ctx.patchState({inProgress: false}))
      );
  }

  @Action(ServiceProviderDetailAction.Save)
  save(ctx: StateContext<ServiceProviderDetailStateModel>): Observable<void> {
    const state: ServiceProviderDetailStateModel = ctx.getState();

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    ctx.patchState({inProgress: true});

    const request = this.serviceProviderRequestService
      .updateServiceProvider(state.updatedServiceProvider);

    ctx.patchState({inProgress: true});

    return request
      .pipe(
        map((serviceProvider: ServiceProviderModel) => {
          ctx.patchState({inProgress: false});
          ctx.patchState({serviceProvider: serviceProvider});
        })
      );
  }

  @Action(ServiceProviderDetailAction.UpdateState)
  updateState(
    ctx: StateContext<ServiceProviderDetailStateModel>,
    action: ServiceProviderDetailAction.UpdateState
  ): void {
    const state: ServiceProviderDetailStateModel = ctx.getState();

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    ctx.patchState({updatedServiceProvider: action.serviceProvider});
  }

  @Action([
    ServiceProviderDetailAction.ResetState,
    ResetStateDefaults
  ])
  public resetState(ctx: StateContext<ServiceProviderDetailStateModel>): void {
    ctx.setState(getDefaults);
  }
  //endregion

}
